import { randomInt } from "./rng.ts";
import type { Gender, LogicGameState } from "./types.ts";

export const FAMILY_NAME_CANDIDATES = [
  "黑田", "青柳", "森川", "小林", "藤原",
  "高橋", "佐藤", "橘", "水野", "神谷",
  "朝倉", "白石", "松本", "中村", "星野",
  "伊藤", "山本", "渡邊", "田中", "山田",
  "鈴木", "井上", "木村", "林", "清水",
  "齋藤", "石川", "前田", "岡田", "長谷川",
  "村上", "近藤", "坂本", "遠藤", "西村",
  "藤田", "太田", "三浦", "福田", "岡本",
  "河野", "平野", "久保", "野口", "新井",
];

export const GIVEN_NAME_CANDIDATES: Record<Gender, string[]> = {
  female: [
    "遙", "千夏", "美月", "真白", "陽菜",
    "凜", "澪", "葵", "紬", "光",
    "結衣", "咲", "芽依", "七海", "楓",
    "明日香", "琴音", "美咲", "由奈", "莉子",
    "彩花", "杏", "優衣", "和奏", "雫",
    "小春", "詩織", "玲奈", "沙耶", "美緒",
    "茜", "若葉", "千尋", "佳奈", "瑞希",
    "直美", "理央", "文香", "柚希", "瑠奈",
    "春香", "菜月", "友香", "麻衣", "雪乃",
  ],
  male: [
    "悠", "律", "悠真", "湊", "樹",
    "晴", "蓮", "陸", "蒼", "颯太",
    "直樹", "朔", "大和", "海斗", "旭",
    "拓海", "健太", "翔", "陽介", "誠",
    "和也", "亮", "祐介", "奏太", "伊織",
    "智也", "圭", "修平", "航", "慶太",
    "雅人", "隼人", "拓真", "瑛太", "冬馬",
    "優斗", "匠", "一樹", "怜", "颯",
    "浩介", "陸斗", "蒼真", "康平", "直人",
  ],
};

function draw(state: LogicGameState, values: string[]) {
  const roll = randomInt(state.rng, 0, values.length - 1);
  state.rng = roll.rng;
  return values[roll.value];
}

export function randomNameParts(
  state: LogicGameState,
  gender: Gender,
  options: { familyName?: string; preferDynastyName?: boolean } = {},
) {
  const usedFullNames = new Set(Object.values(state.people).map((person) => person.name));
  const usedGivenNames = new Set(
    Object.values(state.people).map((person) => person.name.replace(state.dynasty.familyName, "")),
  );
  const familyPool = options.familyName
    ? [options.familyName]
    : options.preferDynastyName
      ? [state.dynasty.familyName]
      : FAMILY_NAME_CANDIDATES.filter((name) => name !== state.dynasty.familyName);
  const givenPool = GIVEN_NAME_CANDIDATES[gender];

  for (let attempt = 0; attempt < familyPool.length * givenPool.length; attempt += 1) {
    const family = draw(state, familyPool);
    const given = draw(state, givenPool);
    const full = `${family}${given}`;
    if (!usedFullNames.has(full) && !usedGivenNames.has(given)) {
      return { familyName: family, givenName: given, fullName: full };
    }
  }

  const familyName = options.familyName || state.dynasty.familyName;
  const givenName = `新${state.rng.calls}`;
  return { familyName, givenName, fullName: `${familyName}${givenName}` };
}

export function uniqueFullName(
  state: LogicGameState,
  gender: Gender,
  options: { familyName?: string; preferDynastyName?: boolean } = {},
) {
  return randomNameParts(state, gender, options).fullName;
}
